import { useEffect, useRef } from "react";
import { useAppContext } from "../app_context";

export const useKeyboardVisibility = (disabled = false) => {
	const { appMenuVisible, setAppMenuVisible } = useAppContext();

	// The menu visibility state before keyboard caused the hide
	const prevVisibleRef = useRef(true);

	// Whether the menu is currently hidden BY THIS HOOK
	const hiddenByKeyboardRef = useRef(false);

	// Keep current visibility in a ref to avoid stale closure issues
	const visibleRef = useRef(appMenuVisible);

	useEffect(() => {
		visibleRef.current = appMenuVisible;
	}, [appMenuVisible]);

	useEffect(() => {
		if (disabled) {
			// Restore menu if it was hidden by this hook when becoming disabled
			if (hiddenByKeyboardRef.current) {
				hiddenByKeyboardRef.current = false;
				if (prevVisibleRef.current) {
					setAppMenuVisible(true);
				}
			}
			return;
		}

		// Apply only on mobile / coarse pointer devices
		const mq = window.matchMedia("(pointer: coarse)");
		if (!mq.matches) return;

		const isTextInput = el => {
			if (!el) return false;

			const tag = el.tagName?.toLowerCase?.();
			if (!tag) return false;

			// contenteditable also triggers virtual keyboard
			if (el.isContentEditable) return true;

			if (tag === "textarea") return true;
			if (tag !== "input") return false;

			const type = (el.type || "text").toLowerCase();

			// Exclude non-text input types
			return ![
				"button",
				"submit",
				"reset",
				"checkbox",
				"radio",
				"file",
				"image",
				"range",
				"color",
				"date",
				"datetime-local",
				"month",
				"week",
				"time",
				"hidden",
			].includes(type);
		};

		// Hide menu and save previous state (only on the FIRST hide)
		const hideMenu = () => {
			if (!hiddenByKeyboardRef.current) {
				prevVisibleRef.current = !!visibleRef.current;
				hiddenByKeyboardRef.current = true;
			}
			if (visibleRef.current !== false) {
				setAppMenuVisible(false);
			}
		};

		// Restore menu to the state it was in before keyboard opened
		const restoreMenu = () => {
			if (hiddenByKeyboardRef.current) {
				hiddenByKeyboardRef.current = false;
				if (prevVisibleRef.current && visibleRef.current !== true) {
					setAppMenuVisible(true);
				}
			}
		};

		// Sync menu visibility with actual focus state
		const sync = () => {
			const active = document.activeElement;
			if (isTextInput(active)) {
				hideMenu();
			} else {
				restoreMenu();
			}
		};

		const onFocusIn = e => {
			if (!isTextInput(e.target)) return;
			hideMenu();
		};

		const onFocusOut = () => {
			// Delay allows switching between inputs without flicker
			setTimeout(sync, 80);
		};

		document.addEventListener("focusin", onFocusIn, true);
		document.addEventListener("focusout", onFocusOut, true);

		// PWA / iOS edge-case protections
		document.addEventListener("visibilitychange", sync);
		window.addEventListener("pagehide", sync);
		window.addEventListener("pageshow", sync);

		// Detect virtual keyboard via visualViewport resize
		const vv = window.visualViewport;
		vv?.addEventListener("resize", sync);

		// Initial state sync
		sync();

		return () => {
			document.removeEventListener("focusin", onFocusIn, true);
			document.removeEventListener("focusout", onFocusOut, true);

			document.removeEventListener("visibilitychange", sync);
			window.removeEventListener("pagehide", sync);
			window.removeEventListener("pageshow", sync);

			vv?.removeEventListener("resize", sync);

			// Restore menu on cleanup if hidden by this hook
			if (hiddenByKeyboardRef.current) {
				hiddenByKeyboardRef.current = false;
				if (prevVisibleRef.current) {
					setAppMenuVisible(true);
				}
			}
		};
	}, [disabled, setAppMenuVisible]);
};
